/* global JQuery */
import { FormGroup2, FormRow2, Input2, InputType2, LangTextV2, ModalDialog2, ModalDialogType2, Select2 } from 'bambooo';
import { boundingExtent } from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import { Lang } from '../../Lang';
import { BaseMap } from '../../Map/BaseMap';
import { OsmBaseLayer } from '../../Map/Layers/OsmBaseLayer';
import { SightingPointsLayer } from '../../Map/Layers/SightingPointsLayer';
import { SightingMapObjectType } from '../../Map/Styles/SightingStyles';
const ALL_OPTION = { key: '0', value: '— any —' };
/**
 * AssignModal
 *
 * Dialog for reassigning the pending-track bucket of an orphan tour_fid
 * to an existing SightingTour. Four pickers (vehicle, driver, date,
 * tour_start) are prefilled from the parsed tour_fid; the host page
 * watches picker changes and pushes a refreshed candidate list via
 * {@link setCandidates}. The user picks a candidate via radio button.
 * Save is enabled only when a candidate is selected.
 */
export class AssignModal extends ModalDialog2 {
    constructor() {
        super({ modalType: ModalDialogType2.xlarge });
        this._tourFid = '';
        this._deviceId = 0;
        this._selectedTargetId = 0;
        this._vehicles = new Map();
        this._drivers = new Map();
        this._onPickerChange = null;
        this._onDeleteRequest = null;
        this._map = null;
        this._trackLayer = null;
        const bodyCard = jQuery('<div class="card-body"/>').appendTo(this.getBody());
        // Pickers row
        const pickerRow = new FormRow2();
        pickerRow.appendTo(bodyCard);
        const groupVehicle = new FormGroup2({ label: new LangTextV2({ text: 'Vehicle' }) });
        groupVehicle.appendTo(pickerRow.createCol(3));
        this._selectVehicle = new Select2({});
        this._selectVehicle.appendTo(groupVehicle);
        const groupDriver = new FormGroup2({ label: new LangTextV2({ text: 'Driver' }) });
        groupDriver.appendTo(pickerRow.createCol(3));
        this._selectDriver = new Select2({});
        this._selectDriver.appendTo(groupDriver);
        const groupDate = new FormGroup2({ label: new LangTextV2({ text: 'Date' }) });
        groupDate.appendTo(pickerRow.createCol(3));
        this._inputDate = new Input2({ type: InputType2.date });
        this._inputDate.appendTo(groupDate);
        const groupStart = new FormGroup2({ label: new LangTextV2({ text: 'Tour start' }) });
        groupStart.appendTo(pickerRow.createCol(3));
        this._inputStart = new Input2({ type: InputType2.time });
        this._inputStart.appendTo(groupStart);
        // Hint above the table
        this._hintInfo = jQuery('<div class="text-muted small pt-2 pb-1"/>').appendTo(bodyCard);
        // Match-table host
        const tableWrap = jQuery('<div class="orphan-match-table" style="max-height: 380px; overflow-y: auto;"/>').appendTo(bodyCard);
        this._matchBody = jQuery('<table class="table table-striped table-sm mb-0"><thead><tr>' +
            '<th style="width: 32px;"></th>' +
            '<th>Id</th>' +
            '<th>Date</th>' +
            '<th>Vehicle</th>' +
            '<th>Driver</th>' +
            '<th>Time</th>' +
            '<th class="text-right">Sightings</th>' +
            '<th class="text-right">Tracks</th>' +
            '</tr></thead><tbody></tbody></table>').appendTo(tableWrap).find('tbody');
        // Map preview of the orphan bucket points + delete-bucket button.
        const previewHeader = jQuery('<div class="d-flex align-items-center justify-content-between pt-3 pb-1"/>').appendTo(bodyCard);
        jQuery('<div class="font-weight-bold"><i class="fa fa-map-marker-alt mr-1"></i> <span data-lang="Orphan track preview">Orphan track preview</span></div>').appendTo(previewHeader);
        this._btnDelete = jQuery('<button type="button" class="btn btn-sm btn-outline-danger">' +
            '<i class="fa fa-trash mr-1"></i><span data-lang="Delete bucket">Delete bucket</span>' +
            '</button>').appendTo(previewHeader);
        this._btnDelete.on('click', async () => {
            if (this._tourFid === '' || this._deviceId === 0) {
                return;
            }
            // eslint-disable-next-line no-alert
            const ok = window.confirm(`Pending-Tracks für ${this._tourFid} (device ${this._deviceId}) endgültig löschen?`);
            if (!ok) {
                return;
            }
            if (this._onDeleteRequest) {
                await this._onDeleteRequest(this._tourFid, this._deviceId);
            }
        });
        this._mapStatsInfo = jQuery('<div class="text-muted small pb-1"/>').appendTo(bodyCard);
        this._mapHost = jQuery('<div style="height: 320px; width: 100%;"/>').appendTo(bodyCard);
        // OL needs an updateSize() once the modal is fully shown, otherwise
        // the canvas measures 0×0 (display:none during construction).
        this._element.on('shown.bs.modal', () => {
            var _a;
            this._ensureMap();
            (_a = this._map) === null || _a === void 0 ? void 0 : _a.updateSize();
        });
        // Re-query on picker changes
        const fire = () => {
            this._selectedTargetId = 0;
            if (this._onPickerChange) {
                this._onPickerChange(this.getVehicleId(), this.getDriverId(), this.getDate(), this.getTourStart());
            }
            this._updateSaveEnabled();
        };
        this._selectVehicle.setChangeFn(fire);
        this._selectDriver.setChangeFn(fire);
        this._inputDate.getElement().on('change', fire);
        this._inputStart.getElement().on('change', fire);
        this.addButtonClose(Lang.i().l('Close'));
        this.addButtonSave(Lang.i().l('Assign tracks'), true);
        this._updateSaveEnabled();
    }
    setVehicleList(list) {
        this._vehicles.clear();
        for (const v of list) {
            this._vehicles.set(v.id, v);
        }
        this._selectVehicle.clearValues();
        this._selectVehicle.setValues([ALL_OPTION, ...list.map((v) => ({ key: `${v.id}`, value: v.name }))]);
        this._selectVehicle.setSelectedValue('0');
    }
    setDriverList(list) {
        this._drivers.clear();
        for (const d of list) {
            this._drivers.set(d.id, d);
        }
        this._selectDriver.clearValues();
        this._selectDriver.setValues([ALL_OPTION, ...list.map((d) => ({ key: `${d.id}`, value: d.user.name }))]);
        this._selectDriver.setSelectedValue('0');
    }
    setSource(tourFid, deviceId, vehicleId, vehicleDriverId, date, tourStart) {
        this._tourFid = tourFid;
        this._deviceId = deviceId;
        this._selectVehicle.setSelectedValue(`${vehicleId}`);
        this._selectDriver.setSelectedValue(`${vehicleDriverId}`);
        this._inputDate.setValue(date);
        this._inputStart.setValue(tourStart);
        this._selectedTargetId = 0;
        this._hintInfo.text(`Source bucket: ${tourFid} (device ${deviceId})`);
        this._updateSaveEnabled();
    }
    setOnPickerChange(fn) {
        this._onPickerChange = fn;
    }
    setOnDeleteRequest(fn) {
        this._onDeleteRequest = fn;
    }
    /**
     * Lazy-init the OL preview map. Called from the `shown.bs.modal`
     * handler so the map only spins up the first time the dialog is
     * actually opened.
     */
    _ensureMap() {
        if (this._map) {
            return;
        }
        this._map = new BaseMap(this._mapHost[0]);
        this._map.setHeight(320);
        this._map.load({ initialCenterLonLat: [-15, 28], initialZoom: 5, withLayerSwitcher: false });
        this._map.addLayer(new OsmBaseLayer());
        this._trackLayer = new SightingPointsLayer();
        this._map.addLayer(this._trackLayer);
    }
    /**
     * Render the orphan bucket's points onto the preview map. Draws a
     * polyline through all points + start/end markers, then fits the
     * view to the bbox. Called from the host page after the bucket is
     * opened in the modal.
     */
    setPoints(points) {
        this._ensureMap();
        if (!this._map || !this._trackLayer) {
            return;
        }
        this._trackLayer.clearFeatures();
        if (points.length === 0) {
            this._trackLayer.refresh();
            this._mapStatsInfo.text(Lang.i().l('No decodable points in this bucket'));
            return;
        }
        const coords = points.map((p) => [p.lon, p.lat]);
        this._trackLayer.addLineRoute(coords);
        this._trackLayer.addSighting(SightingMapObjectType.Start, 'orphan-start', '', coords[0]);
        if (coords.length > 1) {
            this._trackLayer.addSighting(SightingMapObjectType.End, 'orphan-end', '', coords[coords.length - 1]);
        }
        this._trackLayer.refresh();
        const olMap = this._map.getOlMap();
        if (olMap) {
            const extent = boundingExtent(coords.map((c) => fromLonLat(c)));
            olMap.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 13 });
        }
        const minTs = points[0].ts;
        const maxTs = points[points.length - 1].ts;
        const span = Math.max(0, maxTs - minTs);
        const minStr = AssignModal._formatTs(minTs);
        const maxStr = AssignModal._formatTs(maxTs);
        this._mapStatsInfo.text(`${points.length} points · ${minStr} – ${maxStr} (Δ ${AssignModal._formatSpan(span)})`);
    }
    static _formatTs(ts) {
        if (!Number.isFinite(ts) || ts <= 0) {
            return '—';
        }
        const d = new Date(ts * 1000);
        return d.toISOString().replace('T', ' ').slice(0, 16);
    }
    static _formatSpan(seconds) {
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const min = Math.floor(seconds / 60);
        if (min < 60) {
            return `${min} min`;
        }
        const h = Math.floor(min / 60);
        const m = min % 60;
        return `${h}h ${m}m`;
    }
    getVehicleId() {
        return parseInt(this._selectVehicle.getSelectedValue() || '0', 10) || 0;
    }
    getDriverId() {
        return parseInt(this._selectDriver.getSelectedValue() || '0', 10) || 0;
    }
    getDate() {
        return this._inputDate.getValue().trim();
    }
    getTourStart() {
        return this._inputStart.getValue().trim();
    }
    getValues() {
        return {
            tour_fid: this._tourFid,
            device_id: this._deviceId,
            vehicle_id: this.getVehicleId(),
            vehicle_driver_id: this.getDriverId(),
            date: this.getDate(),
            tour_start: this.getTourStart(),
            target_tour_id: this._selectedTargetId
        };
    }
    getTargetTourId() {
        return this._selectedTargetId;
    }
    setCandidates(candidates) {
        var _a, _b, _c, _d;
        this._matchBody.empty();
        if (candidates.length === 0) {
            this._matchBody.append(`<tr><td colspan="8" class="text-center text-muted">${Lang.i().l('No matching tours — relax a picker.')}</td></tr>`);
            this._selectedTargetId = 0;
            this._updateSaveEnabled();
            return;
        }
        for (const c of candidates) {
            const vehicleName = (_b = (_a = this._vehicles.get(c.vehicle_id)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : `#${c.vehicle_id}`;
            const driverName = (_d = (_c = this._drivers.get(c.vehicle_driver_id)) === null || _c === void 0 ? void 0 : _c.user.name) !== null && _d !== void 0 ? _d : `#${c.vehicle_driver_id}`;
            const escape = (s) => s.replace(/[&<>"']/g, (ch) => { var _a; return (_a = ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' })[ch]) !== null && _a !== void 0 ? _a : ch; });
            const row = jQuery('<tr>' +
                `<td><input type="radio" name="orphantracks-target" value="${c.id}"></td>` +
                `<td>#${c.id}</td>` +
                `<td>${escape(c.date)}</td>` +
                `<td>${escape(vehicleName)}</td>` +
                `<td>${escape(driverName)}</td>` +
                `<td>${escape(c.tour_start)} – ${escape(c.tour_end)}</td>` +
                `<td class="text-right">${c.count_sightings}</td>` +
                `<td class="text-right">${c.count_trackings}</td>` +
                '</tr>');
            row.find('input[type=radio]').on('change', () => {
                this._selectedTargetId = c.id;
                this._updateSaveEnabled();
            });
            this._matchBody.append(row);
        }
    }
    resetValues() {
        this._tourFid = '';
        this._deviceId = 0;
        this._selectVehicle.setSelectedValue('0');
        this._selectDriver.setSelectedValue('0');
        this._inputDate.setValue('');
        this._inputStart.setValue('');
        this._matchBody.empty();
        this._selectedTargetId = 0;
        this._hintInfo.text('');
        this._mapStatsInfo.text('');
        if (this._trackLayer) {
            this._trackLayer.clearFeatures();
            this._trackLayer.refresh();
        }
        this._updateSaveEnabled();
    }
    _updateSaveEnabled() {
        const btn = this.getButtonSave();
        if (!btn) {
            return;
        }
        if (this._selectedTargetId > 0) {
            btn.getElement().removeAttr('disabled');
        }
        else {
            btn.getElement().attr('disabled', 'disabled');
        }
    }
}
